import { isNull } from 'lodash';
import { map } from 'rxjs/operators';
import isEmpty from 'lodash/isEmpty';
import { Observable, of } from 'rxjs';

import { DomSanitizer } from '@angular/platform-browser';
import { Injectable, SecurityContext } from '@angular/core';

import { ServerApi } from './server-api';
import { ComplianceFeatureFlagService } from './feature-flags/compliance';

import { fullName, mapEventStatus, mapDrivingEventType, extractResponseBody, IPagination } from '@lib/utils';
import { mapRestrictionReason, RESTRICTION_REASON } from '@lib/data/assign-restriction-reason';
import { moment, IMoment } from '@lib/moment';
import { shouldNotShowSTMSuggestion } from '@lib/compliance-utils';
import { DriverIdOriginTags } from '@lib/data/driver-identification';
import { mapDriverTagData } from '@misc/driver-tag/driver-tag';

import { ELD_MODES } from '@lib/eld-modes';
import { VEHICLE_REGULATION_MODES } from '@lib/vehicle-regulation-modes';
import { TRIP_TRIGGER_SOURCE } from '@lib/data/driving-period-view-model';
import { IPaginationInfo, Pagination } from '@lib/pagination';
import { IDriverTagData } from '@misc/driver-tag/driver-tag.interface';
import { PAGE_NO_KEY, PER_PAGE_KEY } from '@lib/state-manager';

export enum DRIVER_SUGGESTED_BY {
  'DRIVER_ID',
  'SMART_TRIP_MATCHING',
  'FACE_MATCH',
}

export enum States {
  Nil,
  Approved,
  Pending,
  PendingWithAssignment,
}

export const DRIVER_SUGGESTED_BY_MAPPING = {
  driver_id: DRIVER_SUGGESTED_BY.DRIVER_ID,
  smart_trip_matching: DRIVER_SUGGESTED_BY.SMART_TRIP_MATCHING,
  face_match: DRIVER_SUGGESTED_BY.FACE_MATCH,
};

export interface IServerSuggestedDriver {
  bounded_image_url: string,
  bounding_box: string,
  first_name: string,
  id: number,
  driver_facial_profile_id: number,
  image_datetime: string,
  last_name: string,
  suggested_by: string,
  driver_company_id: string,
  eld_mode?: ELD_MODES,
  duty_status?: string,
}

export interface IServerFacialProfileDriver {
  facial_profile: {
    profile_id: number,
    driver_description: string,
    is_non_company_driver: boolean,
    status: string,
    vehicle_groups: number[],
    driver: {
      id: string,
      first_name: string,
      last_name: string,
      deactivated_at: string,
    },
  }
}

export interface IFacialProfileIdentifiedDriverInfo {
  driverId: string,
  deactivatedTimeStamp: string,
}

export interface IFacialProfileDriver {
  profileId: number,
  nonCompanyDriverDescription: string,
  isNonCompanyDriver: boolean,
  status: string,
  driver?: IFacialProfileIdentifiedDriverInfo,
  driverName?: string,
  vehicleGroups?: number[],
}

export interface ISuggestedDriver {
  id: number;
  boundedImageUrl: string;
  name: string;
  suggestedBy: DRIVER_SUGGESTED_BY;
  imageCapturedAt?: IMoment;
  profileId?: number;
  firstName?: string;
  imageUrl?: string;
  imageDate?: IMoment;
  aiSuggestionParams?: string[];
  isAssignable?: boolean;
  driverCompanyId?: string;
  dutyStatus?: string;
  eldMode?: string;
}

export interface IDateRange {
  start: string;
  end: string;
}

export interface ITripDriver {
  id: number;
  driverCompanyId?: number | string;
  name: string;
  eldMode: ELD_MODES;
  firstName?: string;
  lastName?: string;
}

export interface ITripsChangedAttributes {
  notes: string;
  noteAuthor: string;
  noteAuthorId?: number;
  driver: ITripDriver;
}

export interface ITripsLabelData {
  driver: any;
  suggestedDriver: IServerSuggestedDriver;
  notes: string;
  changedAttributes: any;
  dispatcher: IDispatcher;
  vehicleNumber: number | string;
  isClaimed: boolean;
  identifiedByIdCard?: boolean;
  driverIdOriginTag?: DriverIdOriginTags;
}

export interface IDispatcher {
  name: string;
  id: number;
}

interface IResponseCache {
  profileId?: IFacialProfileDriver;
}

export interface ITrip {
  id: number;
  startTime: IMoment;
  endTime: IMoment;
  driver: ITripDriver;
  notes: string;
  trigger?: TRIP_TRIGGER_SOURCE;
  dispatcher: IDispatcher;
  changedAttributes: ITripsChangedAttributes;
  vehicleNumber: number | string;
  vehicleId?: number;
  oemNumber?: number | string;
  vehicleGroupIds?: number[];
  oemId?: number;
  isOemEntity?: boolean;
  oemGroupIds?: number[];
  origin: string;
  duration: number;
  distance: number;
  status: string;
  actualType: string;
  type: string;
  disableSelection: boolean;
  isClaimed: boolean;
  logIds: number[];
  startEventOfflineId: string;
  isUnregulated: boolean;
  suggestedDriver?: ISuggestedDriver;
  driverTagData?: IDriverTagData;
  restrictionReason: RESTRICTION_REASON;
  labelAndTooltipData?: ITripsLabelData;
  isDispatcherMotive?: boolean;
  unassignable: boolean;
  noteState?: States;
  selected?: boolean;
}

export interface ITripsListData {
  listItems: ITrip[];
  pagination: IPagination;
}

const ENDPOINT = 'driving_periods';
const ITEMS_PER_PAGE = 50;
const API_VER_W3 = 'w3';
const API_VER_W2 = 'w2';
const DEFAULT_SORT = {
  field: 'start_time',
  direction: 'desc',
};
export const MAX_SELECTION_DAYS_ALLOWED = 7;

@Injectable({providedIn: 'root'})
export class TripsList {
  private isSTMEnabled: boolean;
  private STATE = States;
  private facialProfileResponseCache: IResponseCache = {};
  private isTripListFEPaginationEnabled: boolean = false;

  constructor(
    private api: ServerApi,
    private sanitizer: DomSanitizer,
    private complianceFeatureFlagService: ComplianceFeatureFlagService,
  ) {
    this.isSTMEnabled = this.complianceFeatureFlagService.isSTMEnabled();
    this.isTripListFEPaginationEnabled = this.complianceFeatureFlagService.isTripListFEPaginationEnabled();
  }

  public get(params = null): Observable<ITripsListData> {
    params = params ? new Map(params) : new Map();
    const apiVersion = this.complianceFeatureFlagService.isComplianceTripsNewAILabelsPhaseTwoEnabled() ? API_VER_W3 : API_VER_W2;
    return this.api.get(ENDPOINT, this._getParams(params),apiVersion)
    .pipe(
      map(extractResponseBody),
      map(res =>
        this.transformData(
          res,
          { start: params.get('start_date'), end: params.get('end_date') },
          { perPage: params.get(PER_PAGE_KEY), pageNo: params.get(PAGE_NO_KEY) },
        ),
      ),
    );
  }

  public isMaximumDaysAllowedRangeExceeded(dateRange: IDateRange): boolean {
    if (!dateRange) return true;
    const selectedRangeInDays: number = moment().range(dateRange.start, dateRange.end).diff('days');
    return selectedRangeInDays > MAX_SELECTION_DAYS_ALLOWED;
  }

  private _getParams(params) {
    if (params.get('sort_field') == null) params.set('sort_field', DEFAULT_SORT.field);
    if (params.get('sort_direction') == null) params.set('sort_direction', DEFAULT_SORT.direction);
    params.set('per_page', ITEMS_PER_PAGE);
    return params;
  }

  public transformData(data, dateRange: IDateRange, paginationInfo: IPaginationInfo): ITripsListData {
    const drivingPeriodList: ITrip[] = data.driving_periods.map(dp => {
      const drivingPeriod = dp.driving_period;
      let suggestedDriverServer: IServerSuggestedDriver = drivingPeriod.suggested_driver;
      const driver: any = drivingPeriod.driver || {};
      const changedAttributes = Object.keys(drivingPeriod.changed_attributes).length ? drivingPeriod.changed_attributes : null;
      const dispatcher = drivingPeriod.dispatcher ?
        { name: fullName(drivingPeriod.dispatcher), id: drivingPeriod.dispatcher.id } : null;

      if (shouldNotShowSTMSuggestion(suggestedDriverServer, this.isSTMEnabled)) {
        suggestedDriverServer = null;
      }

      if (this.isMaximumDaysAllowedRangeExceeded(dateRange)) {
        drivingPeriod.restriction_reason = 'maximum_selected_days_range_exceeded';
      }

      const suggestedDriver: ISuggestedDriver = (suggestedDriverServer && suggestedDriverServer.id)
      || (suggestedDriverServer && suggestedDriverServer.driver_facial_profile_id
          && this.complianceFeatureFlagService.isComplianceTripsNewAILabelsPhaseTwoEnabled()) ? {
            id: suggestedDriverServer.id,
            profileId: suggestedDriverServer.driver_facial_profile_id,
            boundedImageUrl: suggestedDriverServer.bounded_image_url,
            imageCapturedAt: moment(this.sanitizer.sanitize(SecurityContext.HTML, suggestedDriverServer.image_datetime)),
            name: fullName(suggestedDriverServer),
            suggestedBy: DRIVER_SUGGESTED_BY_MAPPING[suggestedDriverServer.suggested_by],
            isAssignable: drivingPeriod.assignable,
            driverCompanyId: suggestedDriverServer.driver_company_id ?? '',
          } : null
      ;

      return {
        id: drivingPeriod.id,
        startTime: moment(drivingPeriod.start_time),
        endTime: moment(drivingPeriod.end_time),
        driver: {
          id: driver.id,
          driverCompanyId: driver?.driver_company_id,
          name: fullName(driver),
          firstName: driver.first_name,
          eldMode: driver.eld_mode,
        },
        notes: drivingPeriod.notes,
        dispatcher,
        changedAttributes: this.changedAttributes(changedAttributes),
        vehicleNumber: drivingPeriod.vehicle?.number,
        vehicleId: drivingPeriod.vehicle?.id,
        vehicleGroupIds: drivingPeriod.vehicle?.group_ids,
        oemNumber: drivingPeriod.oem_entity?.number,
        oemId: drivingPeriod.oem_entity?.id,
        oemGroupIds: drivingPeriod.oem_entity?.group_ids,
        trigger: drivingPeriod.trigger,
        isOemEntity: !!drivingPeriod.oem_entity,
        origin: drivingPeriod.start_address || drivingPeriod.start_location,
        duration: moment(drivingPeriod.end_time || undefined).diff(moment(drivingPeriod.start_time), 'seconds'),
        distance: drivingPeriod.distance,
        status: mapEventStatus(drivingPeriod.status),
        actualType: drivingPeriod.type,
        type: mapDrivingEventType(drivingPeriod.type),
        disableSelection: this.isTripDisabled(drivingPeriod, dateRange),
        restrictionReason: mapRestrictionReason(drivingPeriod.restriction_reason),
        isClaimed: drivingPeriod.is_claimed,
        logIds: drivingPeriod.log_ids && drivingPeriod.log_ids.length > 0 ? drivingPeriod.log_ids : null,
        startEventOfflineId: drivingPeriod.start_event_offline_id,
        suggestedDriver,
        isUnregulated: drivingPeriod.regulation_mode === VEHICLE_REGULATION_MODES.UNREGULATED,
        noteState: this.getNoteState(drivingPeriod),
        isDispatcherMotive: this.checkIfDispatcherMotive(drivingPeriod),
        labelAndTooltipData: {
          driver,
          suggestedDriver: (suggestedDriverServer && suggestedDriverServer.id)
            || (suggestedDriverServer && suggestedDriverServer.driver_facial_profile_id
            && this.complianceFeatureFlagService.isComplianceTripsNewAILabelsPhaseTwoEnabled()) ? suggestedDriverServer : null,
          notes: drivingPeriod.notes,
          changedAttributes,
          dispatcher,
          vehicleNumber: drivingPeriod.vehicle?.number,
          isClaimed: drivingPeriod.is_claimed,
          identifiedByIdCard: drivingPeriod.driver && drivingPeriod.driver_id_origin_tag === DriverIdOriginTags.rfid,
          driverIdOriginTag: drivingPeriod.driver_id_origin_tag,
        },
        ...(this.complianceFeatureFlagService.isDriverIdRealTimeFaceMatchTripsIntegrationEnabled() && {
          driverTagData: mapDriverTagData({
            serverDriver: isEmpty(driver) ? this.changedAttributes(changedAttributes)?.driver : driver,
            serverSuggestedDriver: suggestedDriverServer,
            dispatcherName: dispatcher?.name || this.changedAttributes(changedAttributes)?.noteAuthor,
            vehicleNumber: drivingPeriod.vehicle?.number,
            driverIdOriginTag: drivingPeriod.driver_id_origin_tag,
          })
        }),
        unassignable: drivingPeriod.unassignable,
      };
    });

    let pagination: IPagination;

    if (this.isTripListFEPaginationEnabled || !data.total) {
      pagination = new Pagination(paginationInfo, data.driving_periods.length).getPagination();
    } else {
      pagination = {
        perPage: parseInt(data.per_page, 10),
        totalItemsInLastResponse: data.driving_periods.length,
        pageNo: parseInt(data.page_no, 10),
        totalItems: parseInt(data.total, 10),
        noOfPages: Math.ceil(data.total / data.per_page),
      };
    }

    return {
      listItems: drivingPeriodList,
      pagination,
    };
  }

  private isTripDisabled(drivingPeriod, dateRange: IDateRange): boolean {
    return !drivingPeriod.editable || this.isMaximumDaysAllowedRangeExceeded(dateRange);
  }

  private changedAttributes(attrs): ITripsChangedAttributes {
    if (attrs) {
      return {
        notes: attrs.notes,
        noteAuthor: attrs.notes_added_by ? fullName(attrs.notes_added_by) : null,
        noteAuthorId: attrs.notes_added_by?.id,
        driver: attrs.driver ? {
          id: attrs.driver.id,
          name: fullName(attrs.driver),
          firstName: attrs.driver.first_name,
          lastName: attrs.driver.last_name,
          eldMode: attrs.driver.eld_mode,
          driverCompanyId: attrs.driver.driver_company_id,
        } : null,
      };
    }
    return null;
  }

  private getNoteState(drivingPeriod): States {
    let valueToRet = States.Nil;
    if (!!drivingPeriod.notes && !Object.keys(drivingPeriod.changed_attributes).length) {
      valueToRet = this.STATE.Approved;
    } else if (!!drivingPeriod.changed_attributes?.notes_added_by?.id) {
      valueToRet = this.STATE.Pending;
    }
    return valueToRet;
  }

  private checkIfDispatcherMotive(drivingPeriod): boolean {
    const dispatcherId = drivingPeriod.dispatcher ?
    drivingPeriod.dispatcher.id : !!drivingPeriod.changed_attributes?.notes_added_by?.id ?
    drivingPeriod.changed_attributes.notes_added_by.id : null;
    return dispatcherId === MOTIVE_ID;
  }
}

